import React, { useState, useMemo } from "react";
import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PrintIcon from "@mui/icons-material/Print";
import InventoryIcon from "@mui/icons-material/Inventory";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import SupplyProgressBar from "components/Supply/ProgressBar";
import LanguageText from "components/Supply/LanguageText";
import { usePendingSupply, useSupplyProgress, useMarkSupplied } from "api/useSupply.ts";
import { useTranslation } from "react-i18next";

function Supply() {
  const { t } = useTranslation();
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const { data: pendingData, isLoading: pendingLoading, error: pendingError } = usePendingSupply();
  const {
    data: progressData,
    isLoading: progressLoading,
    error: progressError,
  } = useSupplyProgress("product");
  const markSuppliedMutation = useMarkSupplied();

  const isLoading = pendingLoading || progressLoading;
  const error = pendingError || progressError;

  const filteredData = useMemo(() => {
    if (
      !pendingData ||
      !Array.isArray(pendingData) ||
      !progressData ||
      !Array.isArray(progressData)
    ) {
      return [];
    }

    // Combine pending data with progress data
    const combinedData = pendingData.map((pendingItem) => {
      const progressItem = progressData.find(
        (progress) => progress.packProductId === pendingItem.productId
      );

      return {
        ...pendingItem,
        progress: progressItem?.progress || 0,
        threshold: progressItem?.minSupplyQuantity || 0,
        accumulatedQuantity: progressItem?.accumulatedQuantity || 0,
        contributingProducts: progressItem?.contributingProducts || [],
      };
    });

    let filtered = [...combinedData];

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((productGroup) => {
        if (productGroup.productName?.toLowerCase().includes(searchLower)) {
          return true;
        }
        return productGroup.orders.some(
          (order) =>
            order.mosque.mosqueName.toLowerCase().includes(searchLower) ||
            order.mosque.responsibleName.toLowerCase().includes(searchLower)
        );
      });
    }

    return filtered;
  }, [pendingData, progressData, searchText]);

  const handleOrderSelection = (orderId, isSelected) => {
    const newSelectedOrders = new Set(selectedOrders);
    if (isSelected) {
      newSelectedOrders.add(orderId);
    } else {
      newSelectedOrders.delete(orderId);
    }
    setSelectedOrders(newSelectedOrders);
  };

  const handleProductSelection = (productId, isSelected) => {
    const newSelectedOrders = new Set(selectedOrders);
    const productGroup = filteredData.find((pg) => pg.productId === productId);

    if (productGroup) {
      productGroup.orders.forEach((order) => {
        if (isSelected) {
          newSelectedOrders.add(order.orderId);
        } else {
          newSelectedOrders.delete(order.orderId);
        }
      });
    }
    setSelectedOrders(newSelectedOrders);
  };

  const isProductFullySelected = (productId) => {
    const productGroup = filteredData.find((pg) => pg.productId === productId);
    if (!productGroup) return false;

    return productGroup.orders.every((order) => selectedOrders.has(order.orderId));
  };

  const isProductPartiallySelected = (productId) => {
    const productGroup = filteredData.find((pg) => pg.productId === productId);
    if (!productGroup) return false;

    const selectedCount = productGroup.orders.filter((order) =>
      selectedOrders.has(order.orderId)
    ).length;
    return selectedCount > 0 && selectedCount < productGroup.orders.length;
  };

  const handleSelectAll = () => {
    if (selectedOrders.size === getTotalOrderCount()) {
      setSelectedOrders(new Set());
    } else {
      const allOrders = new Set();
      filteredData.forEach((productGroup) => {
        productGroup.orders.forEach((order) => {
          allOrders.add(order.orderId);
        });
      });
      setSelectedOrders(allOrders);
    }
  };

  const getTotalOrderCount = () => {
    return filteredData.reduce((total, productGroup) => total + productGroup.orders.length, 0);
  };

  const handleMarkAsSupplied = async () => {
    try {
      const ordersToMark = Array.from(selectedOrders);
      for (const orderId of ordersToMark) {
        await markSuppliedMutation.mutateAsync({ orderId });
      }
      setNotification({
        open: true,
        message: t("supply.successMessage", { count: ordersToMark.length }),
        severity: "success",
      });
      setSelectedOrders(new Set());
      setIsConfirmModalOpen(false);
    } catch (error) {
      setNotification({
        open: true,
        message: `${t("supply.errorMessage")}: ${error.message}`,
        severity: "error",
      });
    }
  };

  const handlePrintSupplyOrder = () => {
    const selectedOrdersList = Array.from(selectedOrders);
    const selectedOrdersData = [];

    // Get the actual order data for selected orders
    filteredData.forEach((productGroup) => {
      productGroup.orders.forEach((order) => {
        if (selectedOrdersList.includes(order.orderId)) {
          selectedOrdersData.push({
            ...order,
            productName: productGroup.productName,
          });
        }
      });
    });

    // Group orders by mosque
    const groupedForRequest = {};
    selectedOrdersData.forEach((order) => {
      const mosqueId = order.mosque.mosqueId;
      if (!groupedForRequest[mosqueId]) {
        groupedForRequest[mosqueId] = {
          mosque: order.mosque,
          orders: [],
        };
      }
      groupedForRequest[mosqueId].orders.push(order);
    });

    // Create print content
    const printContent = createPrintContent(groupedForRequest);

    // Open print window
    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const createPrintContent = (groupedForRequest) => {
    const currentDate = new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    let content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Supply Order - ${currentDate}</title>
        <style>
          @media print {
            @page {
              margin: 0.5in;
              size: A4;
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 12px;
              line-height: 1.4;
              color: #000;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #000;
              padding-bottom: 20px;
            }
            .company-name {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 10px;
            }
            .document-title {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 10px;
            }
            .date {
              font-size: 14px;
            }
            .mosque-section {
              margin-bottom: 30px;
              page-break-inside: avoid;
            }
            .mosque-header {
              background-color: #f5f5f5;
              padding: 10px;
              border: 1px solid #ddd;
              margin-bottom: 15px;
            }
            .mosque-name {
              font-size: 16px;
              font-weight: bold;
            }
            .mosque-details {
              display: flex;
              justify-content: space-between;
              margin-bottom: 15px;
            }
            .detail-item {
              margin-bottom: 5px;
            }
            .detail-label {
              font-weight: bold;
            }
            .orders-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
            }
            .orders-table th,
            .orders-table td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            .orders-table th {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            .order-id {
              font-family: monospace;
            }
            .signature-section {
              display: flex;
              justify-content: space-between;
              margin-top: 40px;
              margin-bottom: 20px;
            }
            .signature-box {
              width: 200px;
              text-align: center;
            }
            .footer {
              text-align: center;
              font-size: 10px;
              color: #666;
              margin-top: 30px;
              border-top: 1px solid #ddd;
              padding-top: 10px;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">${t("supply.print.companyName")}</div>
          <div class="document-title">${t("supply.print.title")}</div>
          <div class="date">${t("supply.print.date")}: ${currentDate}</div>
        </div>
    `;

    Object.values(groupedForRequest).forEach((group) => {
      content += `
        <div class="mosque-section">
          <div class="mosque-header">
            <div class="mosque-name">${group.mosque.mosqueName}</div>
          </div>
          
          <div class="mosque-details">
            <div>
              <div class="detail-item">
                <span class="detail-label">${t("supply.print.responsiblePerson")}:</span> ${
        group.mosque.responsibleName
      }
              </div>
              <div class="detail-item">
                <span class="detail-label">${t("supply.print.primaryPhone")}:</span> ${
        group.mosque.mobileNumber
      }
              </div>
              ${
                group.mosque.secondNumber
                  ? `
                <div class="detail-item">
                  <span class="detail-label">${t("supply.print.secondaryPhone")}:</span> ${
                      group.mosque.secondNumber
                    }
                </div>
              `
                  : ""
              }
            </div>
            <div>
              <div class="detail-item">
                <span class="detail-label">${t("supply.print.address")}:</span> ${
        group.mosque.address
      }
              </div>
              <div class="detail-item">
                <span class="detail-label">${t("supply.print.city")}:</span> ${group.mosque.city}
              </div>
              ${
                group.mosque.district
                  ? `
                <div class="detail-item">
                  <span class="detail-label">${t("supply.print.district")}:</span> ${
                      group.mosque.district
                    }
                </div>
              `
                  : ""
              }
              ${
                group.mosque.postalCode
                  ? `
                <div class="detail-item">
                  <span class="detail-label">${t("supply.print.postalCode")}:</span> ${
                      group.mosque.postalCode
                    }
                </div>
              `
                  : ""
              }
            </div>
          </div>

          <table class="orders-table">
            <thead>
              <tr>
                <th>${t("supply.print.orderId")}</th>
                <th>${t("supply.print.productId")}</th>
                <th>${t("supply.print.productName")}</th>
                <th>${t("supply.print.supplyQuantity")}</th>
              </tr>
            </thead>
            <tbody>
      `;

      group.orders.forEach((order) => {
        // Calculate total quantity: packs × pack size
        const totalQuantity = (order.quantity || 0) * (order.packQuantity || 1);

        content += `
          <tr>
            <td class="order-id">${order.orderId.slice(-8)}</td>
            <td>${order.orderedProductId || "N/A"}</td>
            <td>${getProductName(order.productName)}</td>
            <td>${totalQuantity}</td>
          </tr>
        `;
      });

      content += `
            </tbody>
          </table>
        </div>
      `;
    });

    content += `
        <div class="signature-section">
          <div class="signature-box">
            <div>${t("supply.print.preparedBy")}</div>
            <div style="margin-top: 20px;">_________________</div>
          </div>
          <div class="signature-box">
            <div>${t("supply.print.receivedBy")}</div>
            <div style="margin-top: 20px;">_________________</div>
          </div>
        </div>

        <div class="footer">
          <p>${t("supply.print.generatedOn")} ${currentDate}</p>
          <p>${t("supply.print.systemName")}</p>
        </div>
      </body>
      </html>
    `;

    return content;
  };

  const openPhoneDialer = (phoneNumber) => {
    window.open(`tel:${phoneNumber}`, "_self");
  };

  const openLocationMap = (location) => {
    if (location?.coordinates) {
      const [lng, lat] = location.coordinates;
      window.open(`https://maps.google.com/?q=${lat},${lng}`, "_blank");
    }
  };

  const getProductName = (productName) => {
    if (typeof productName === "object") {
      return productName.en || productName.ar || "Unknown Product";
    }
    return productName || "Unknown Product";
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox
          pt={6}
          pb={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Alert severity="error">
            {t("supply.errorLoadingRequests")}: {error.message}
          </Alert>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          {/* Enhanced Header Section */}
          <Grid item xs={12}>
            <Card sx={{ mb: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
              <MDBox
                p={4}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                sx={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "rgba(255,255,255,0.1)",
                    backdropFilter: "blur(10px)",
                  },
                }}
              >
                <MDBox position="relative" zIndex={1}>
                  <MDTypography
                    variant="h4"
                    color="white"
                    fontWeight="bold"
                    sx={{ mb: 1, textShadow: "0 2px 4px rgba(0,0,0,0.3)" }}
                  >
                    {t("supply.title")}
                  </MDTypography>
                  <MDTypography
                    variant="body1"
                    color="white"
                    sx={{
                      opacity: 0.9,
                      fontSize: "1.32rem",
                      textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                    }}
                  >
                    {t("supply.description")}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          {/* Enhanced Search and Actions Section */}
          <Grid item xs={12}>
            <Card
              sx={{
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                borderRadius: "16px",
                overflow: "hidden",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3} alignItems="center">
                  {/* Search Section */}
                  <Grid item xs={12} md={6}>
                    <MDBox
                      component="input"
                      type="text"
                      placeholder={t("supply.searchPlaceholder")}
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      sx={{
                        width: "100%",
                        padding: "16px 20px",
                        border: "2px solid #e3f2fd",
                        borderRadius: "12px",
                        fontSize: "21.6px",
                        backgroundColor: "#fafafa",
                        transition: "all 0.3s ease",
                        "&:focus": {
                          outline: "none",
                          borderColor: "#2196f3",
                          backgroundColor: "white",
                          boxShadow: "0 0 0 3px rgba(33, 150, 243, 0.1)",
                          transform: "translateY(-1px)",
                        },
                        "&::placeholder": {
                          color: "#9e9e9e",
                          fontSize: "16.8px",
                        },
                      }}
                    />
                  </Grid>

                  {/* Actions Section */}
                  <Grid item xs={12} md={6}>
                    <MDBox
                      display="flex"
                      flexDirection={{ xs: "column", md: "row" }}
                      gap={2}
                      justifyContent="flex-end"
                      alignItems="center"
                    >
                      {/* Selection Counter */}
                      <MDBox
                        sx={{
                          px: 2,
                          py: 1,
                          borderRadius: "20px",
                          backgroundColor: selectedOrders.size > 0 ? "#e8f5e8" : "#f5f5f5",
                          border: `1px solid ${selectedOrders.size > 0 ? "#4caf50" : "#e0e0e0"}`,
                          transition: "all 0.3s ease",
                        }}
                      >
                        <MDTypography
                          variant="body2"
                          sx={{
                            color: selectedOrders.size > 0 ? "#2e7d32" : "#757575",
                            fontWeight: "600",
                            fontSize: "16.8px",
                          }}
                        >
                          {t("supply.ordersSelected", { count: selectedOrders.size })}
                        </MDTypography>
                      </MDBox>

                      {/* Action Buttons */}
                      <MDBox display="flex" gap={1.5} flexWrap="wrap">
                        <Button
                          variant="outlined"
                          disabled={selectedOrders.size === 0}
                          onClick={handlePrintSupplyOrder}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            px: 3,
                            py: 1.5,
                            borderRadius: "10px",
                            border: "2px solid #e0e0e0",
                            color: "#424242",
                            fontWeight: "600",
                            fontSize: "16.8px",
                            textTransform: "none",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              borderColor: "#2196f3",
                              backgroundColor: "#f3f9ff",
                              color: "#1976d2",
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 12px rgba(33, 150, 243, 0.2)",
                            },
                            "&:disabled": {
                              color: "#bdbdbd",
                              borderColor: "#e0e0e0",
                              backgroundColor: "#fafafa",
                            },
                          }}
                        >
                          <PrintIcon sx={{ fontSize: "21.6px" }} />
                          {t("common.print")}
                        </Button>

                        <Button
                          variant="contained"
                          disabled={selectedOrders.size === 0}
                          onClick={() => setIsConfirmModalOpen(true)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            px: 3,
                            py: 1.5,
                            borderRadius: "10px",
                            background: "linear-gradient(135deg, #4caf50 0%, #45a049 100%)",
                            color: "white",
                            fontWeight: "600",
                            fontSize: "16.8px",
                            textTransform: "none",
                            border: "none",
                            boxShadow: "0 2px 8px rgba(76, 175, 80, 0.3)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              background: "linear-gradient(135deg, #45a049 0%, #388e3c 100%)",
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 16px rgba(76, 175, 80, 0.4)",
                            },
                            "&:disabled": {
                              background: "#e0e0e0",
                              color: "#bdbdbd",
                              boxShadow: "none",
                              transform: "none",
                            },
                          }}
                        >
                          <CheckCircleIcon sx={{ fontSize: "21.6px" }} />
                          {t("supply.markAsSupplied")}
                        </Button>
                      </MDBox>
                    </MDBox>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Enhanced Product Cards */}
          <Grid item xs={12}>
            {filteredData.length === 0 ? (
              <Card
                sx={{
                  p: 4,
                  textAlign: "center",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                  borderRadius: "16px",
                }}
              >
                <Alert
                  severity="info"
                  sx={{
                    fontSize: "19.2px",
                    borderRadius: "12px",
                    "& .MuiAlert-message": {
                      width: "100%",
                    },
                  }}
                >
                  {t("supply.noPendingRequests")}
                </Alert>
              </Card>
            ) : (
              <Grid container spacing={3}>
                {filteredData.map((productGroup, productIndex) => {
                  // Use progress from API (already calculated as percentage)
                  const progressPercentage = productGroup.progress
                    ? productGroup.progress * 100
                    : 0;

                  return (
                    <Grid
                      item
                      xs={12}
                      md={6}
                      lg={4}
                      key={`product-${productGroup.productId}-${productIndex}`}
                    >
                      <Card
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          borderRadius: "16px",
                          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                          transition: "all 0.3s ease",
                          border: "1px solid #f0f0f0",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                            borderColor: "#e0e0e0",
                          },
                        }}
                      >
                        <CardContent sx={{ flexGrow: 1, p: 3 }}>
                          <MDBox display="flex" alignItems="center" mb={3}>
                            <Checkbox
                              checked={isProductFullySelected(productGroup.productId)}
                              indeterminate={isProductPartiallySelected(productGroup.productId)}
                              onChange={(e) =>
                                handleProductSelection(productGroup.productId, e.target.checked)
                              }
                              size="small"
                              sx={{
                                color: "#2196f3",
                                "&.Mui-checked": {
                                  color: "#2196f3",
                                },
                              }}
                            />
                            <InventoryIcon
                              sx={{
                                mr: 1.5,
                                color: "#2196f3",
                                fontSize: "24px",
                              }}
                            />
                            <MDTypography
                              variant="h6"
                              sx={{
                                color: "#212121",
                                fontWeight: "600",
                                fontSize: "1.32rem",
                                lineHeight: 1.3,
                              }}
                            >
                              {typeof productGroup.productName === "object" ? (
                                <LanguageText textMap={productGroup.productName} />
                              ) : (
                                productGroup.productName || t("supply.supplyLayout.unknownProduct")
                              )}
                            </MDTypography>
                          </MDBox>

                          <MDBox mb={2}>
                            <MDTypography variant="body2" color="text" gutterBottom>
                              {t("supply.supplyLayout.progress")}
                            </MDTypography>
                            <SupplyProgressBar progress={progressPercentage / 100} threshold={1} />
                            <MDTypography variant="caption" color="text">
                              {progressPercentage > 100
                                ? `(${t(
                                    "supply.supplyLayout.overThreshold"
                                  )}) ${progressPercentage.toFixed(1)}%`
                                : `${progressPercentage.toFixed(1)}%`}
                            </MDTypography>
                            <MDTypography variant="caption" color="text" display="block">
                              {t("supply.supplyLayout.accumulated")} (
                              {productGroup.accumulatedQuantity || 0} /{" "}
                              {productGroup.threshold || 0}) {progressPercentage.toFixed(1)}%
                            </MDTypography>
                          </MDBox>

                          <MDBox display="flex" justifyContent="space-between" mb={2}>
                            <MDTypography variant="body2" color="text">
                              {t("supply.supplyLayout.orders")}: {productGroup.orderCount}
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              {t("supply.supplyLayout.totalQuantity")}: {productGroup.totalQuantity}
                            </MDTypography>
                          </MDBox>
                        </CardContent>

                        <CardActions sx={{ p: 2, pt: 0 }}>
                          <Accordion sx={{ width: "100%", boxShadow: "none" }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <MDTypography variant="button" color="primary">
                                {t("supply.supplyLayout.viewOrders")} ({productGroup.orders.length})
                              </MDTypography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <MDBox>
                                {productGroup.orders.map((order, index) => (
                                  <MDBox
                                    key={`${order.orderId}-${index}`}
                                    display="flex"
                                    alignItems="center"
                                    p={1}
                                    mb={1}
                                    sx={{
                                      border: "1px solid #e0e0e0",
                                      borderRadius: "8px",
                                      backgroundColor: selectedOrders.has(order.orderId)
                                        ? "#f5f5f5"
                                        : "transparent",
                                    }}
                                  >
                                    <Checkbox
                                      checked={selectedOrders.has(order.orderId)}
                                      onChange={(e) =>
                                        handleOrderSelection(order.orderId, e.target.checked)
                                      }
                                      size="small"
                                    />
                                    <MDBox flex={1} ml={1}>
                                      <MDTypography variant="body2" fontWeight="medium">
                                        {order.mosque.mosqueName}
                                      </MDTypography>
                                      <MDTypography variant="caption" color="text">
                                        {order.mosque.responsibleName}
                                      </MDTypography>
                                      <MDBox display="flex" alignItems="center" gap={1} mt={0.5}>
                                        <MDTypography variant="caption">
                                          {t("supply.supplyLayout.quantity")}:{" "}
                                          {(order.quantity || 0) * (order.packQuantity || 1)}
                                        </MDTypography>
                                        <Tooltip title={t("supply.supplyLayout.call")}>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              openPhoneDialer(order.mosque.mobileNumber)
                                            }
                                          >
                                            <PhoneIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t("supply.supplyLayout.location")}>
                                          <IconButton
                                            size="small"
                                            onClick={() => openLocationMap(order.mosque.location)}
                                            disabled={!order.mosque.location}
                                          >
                                            <LocationOnIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </MDBox>
                                    </MDBox>
                                  </MDBox>
                                ))}
                              </MDBox>
                            </AccordionDetails>
                          </Accordion>
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Grid>
        </Grid>
      </MDBox>

      {/* Confirmation Modal */}
      <Dialog
        open={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("supply.confirmTitle")}</DialogTitle>
        <DialogContent>
          <MDTypography variant="body1" gutterBottom>
            {t("supply.confirmMessage", { count: selectedOrders.size })}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmModalOpen(false)}>{t("supply.cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleMarkAsSupplied}
            disabled={markSuppliedMutation.isPending}
            startIcon={
              markSuppliedMutation.isPending ? <CircularProgress size={20} /> : <CheckCircleIcon />
            }
          >
            {markSuppliedMutation.isPending ? t("supply.processing") : t("supply.confirmSupply")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <MDSnackbar
        color={notification.severity}
        icon={notification.severity === "success" ? "check" : "error"}
        title={notification.severity === "success" ? t("supply.success") : t("supply.error")}
        content={notification.message}
        dateTime={new Date().toLocaleString()}
        open={notification.open}
        onClose={() => setNotification({ ...notification, open: false })}
        close={() => setNotification({ ...notification, open: false })}
        bgWhite
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Supply;
